import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";

@Injectable()
export class PolicyExclusionService extends BaseListService<Models.RecommendationExclusionRulesListRequest, Models.BatchRequest, Models.RecommendationExclusionRule>
{
    public static allowedCostSubClassificationsSet = new Set<Models.PolicyCostSubClassification>([
                                                                                                     Models.PolicyCostSubClassification.Config,
                                                                                                     Models.PolicyCostSubClassification.Idle,
                                                                                                     Models.PolicyCostSubClassification.Optimized,
                                                                                                     Models.PolicyCostSubClassification.Orphaned,
                                                                                                     Models.PolicyCostSubClassification.Underutilized
                                                                                                 ]);
    public static excludedSubClassifications       = new Set<Models.PolicySubClassification>([
                                                                                                 Models.PolicySubClassification.Anomaly,
                                                                                                 Models.PolicySubClassification.Billing,
                                                                                                 Models.PolicySubClassification.Budget,
                                                                                                 Models.PolicySubClassification.Governance,
                                                                                                 Models.PolicySubClassification.Logging,
                                                                                                 Models.PolicySubClassification.Standards,
                                                                                                 Models.PolicySubClassification.Tags,
                                                                                                 Models.PolicySubClassification.Utilization
                                                                                             ]);

    private m_policyClassificationWithSubClassifications: Lookup<Models.PolicyClassification> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.RecommendationExclusionRule);
    }

    @ReportError
    public list(listReq: Models.RecommendationExclusionRulesListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.recommendation.listExclusionRules(listReq);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<Models.RecommendationExclusionRuleBatchResponse>
    {
        const recordIdBatchRequest = Models.BatchRequest.newInstance(req);

        return this.domain.apis.recommendation.batchExclusionRules(recordIdBatchRequest);
    }

    protected createBatchRequest(listReq: Models.RecommendationExclusionRulesListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    public getOptions(): ControlOption<string>[]
    {
        const opts                                              = UtilsService.getEnumValues<Models.PolicyClassification>(Models.PolicyClassification);
        const classificationOptionsNew: ControlOption<string>[] = [];

        opts.forEach(i =>
                     {
                         const subClassification = this.getClassificationMap()[i].subClassification;
                         let enumValues          = UtilsService.getEnumValues<string>(subClassification) as string[];
                         const groupId           = i + "_grp";

                         // While creating/editing a rule for the classification "COST," ensure that only the following sub-classifications are displayed
                         if (groupId === Models.PolicyClassification.Cost + "_grp")
                         {
                             enumValues = enumValues.filter(value => PolicyExclusionService.allowedCostSubClassificationsSet.has(value as Models.PolicyCostSubClassification));
                         }

                         const grp = this.generateOptions(enumValues, i);

                         if (grp.length)
                         {
                             classificationOptionsNew.push(new ControlOption(groupId, i, grp, false, true));
                         }
                     });

        return classificationOptionsNew;
    }

    public getSelectedValues(values: string[]): Models.PolicyClassificationWithSubClassifications
    {
        const data = Models.PolicyClassificationWithSubClassifications.newInstance({});

        values.forEach(value =>
                       {
                           const cls               = this.m_policyClassificationWithSubClassifications[value] as Models.PolicyClassification;
                           const sub_cls           = value.split("__")[1];
                           const classificationMap = this.getClassificationMap();
                           const {accessor}        = classificationMap[cls];
                           accessor(data)
                               .push(sub_cls);
                       });

        return data;
    }

    private getClassificationMap(): {
        [key in Models.PolicyClassification]: {
            subClassification: any;
            accessor: (data: Models.PolicyClassificationWithSubClassifications) => string[];
        };
    }
    {
        return {
            [Models.PolicyClassification.Access]     : {
                subClassification: Models.PolicyAccessSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.access || (data.access = [])
            },
            [Models.PolicyClassification.Cost]       : {
                subClassification: Models.PolicyCostSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.cost || (data.cost = [])
            },
            [Models.PolicyClassification.Data]       : {
                subClassification: Models.PolicyDataSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.data || (data.data = [])
            },
            [Models.PolicyClassification.Network]    : {
                subClassification: Models.PolicyNetworkSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.network || (data.network = [])
            },
            [Models.PolicyClassification.Operation]  : {
                subClassification: Models.PolicyOperationSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.operation || (data.operation = [])
            },
            [Models.PolicyClassification.Resource]   : {
                subClassification: Models.PolicyResourceSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.resource || (data.resource = [])
            },
            [Models.PolicyClassification.Security]   : {
                subClassification: Models.PolicySecuritySubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.security || (data.security = [])
            },
            [Models.PolicyClassification.Performance]: {
                subClassification: Models.PolicyPerformanceSubClassification,
                accessor         : (data: Models.PolicyClassificationWithSubClassifications) => data.performance || (data.performance = [])
            }
        };
    }

    public async getImpactedResources(rule: Models.RecommendationExclusionRule)
    {
        if (rule.resource_scope.service_account_ids?.length)
        {
            let listRecommendationRequest = Models.ListRecommendationRequest.newInstance({
                                                                                             list_context: Models.ListContext.newInstance({
                                                                                                                                              batch_size: 1000
                                                                                                                                          }),
                                                                                             filters     : Models.ListRecommendationFilters.newInstance({
                                                                                                                                                            tenant_ids            : rule.resource_scope.tenant_ids,
                                                                                                                                                            service_names         : [rule.resource_scope.service_name],
                                                                                                                                                            service_account_ids   : rule.resource_scope.service_account_ids,
                                                                                                                                                            policy_classifications: rule.policy_classification
                                                                                                                                                        })
                                                                                         });

            const recommendationListResponse = await this.app.domain.apis.recommendation.listRecommendations(listRecommendationRequest);
            if (recommendationListResponse.results.length)
            {
                const filters                           = Models.ListResourceRecommendationFilters.newInstance({
                                                                                                                   tenant_ids         : rule.resource_scope.tenant_ids,
                                                                                                                   service_account_ids: rule.resource_scope.service_account_ids,
                                                                                                                   recommendation_ids : recommendationListResponse.results,
                                                                                                                   rule_ids           : [rule.rule_id],
                                                                                                                   filters            : rule.resource_scope.filters
                                                                                                               });
                const listResourceRecommendationRequest = Models.ListResourceRecommendationRequest.newInstance({
                                                                                                                   list_context: Models.ListContext.newInstance({
                                                                                                                                                                    batch_size: 1000000
                                                                                                                                                                }),
                                                                                                                   filters     : filters
                                                                                                               });
                return await this.app.domain.apis.recommendation.listResourceRecommendations(listResourceRecommendationRequest);
            }
        }
        return null;
    }

    private generateOptions<T extends string>(subClassifications: T[],
                                              classification: Models.PolicyClassification): ControlOption<string>[]
    {
        const options: ControlOption<string>[] = [];
        subClassifications.forEach((subGrp: T) =>
                                   {
                                       const id = `${classification}__${subGrp}`;
                                       options.push(new ControlOption(id, subGrp));
                                       this.m_policyClassificationWithSubClassifications[id] = classification;
                                   });

        return options;
    }
}
